import { useMemo, useState } from 'react'
import { EventGroup } from '../../hooks/ticket-validation'
import {
  Btn,
  CheckBox,
  FilterBody,
  FilterFooter,
  FilterHeader,
  FilterOverlay,
  FilterRow,
} from './styles'

interface Props {
  eventos: EventGroup[]
  selected: number[]
  onApply: (ids: number[]) => void
  onClose: () => void
}

type CheckState = 'on' | 'off' | 'partial'

const CheckIcon = ({ state }: { state: CheckState }) => {
  if (state === 'on') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
      </svg>
    )
  }
  if (state === 'partial') {
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
        <path d="M6 12h12" />
      </svg>
    )
  }
  return null
}

function stateOf(ids: number[], draft: Set<number>): CheckState {
  if (ids.length === 0) return 'off'
  const on = ids.filter((id) => draft.has(id)).length
  if (on === 0) return 'off'
  if (on === ids.length) return 'on'
  return 'partial'
}

export function SectorFilter({ eventos, selected, onApply, onClose }: Props) {
  const [draft, setDraft] = useState<Set<number>>(new Set(selected))

  const allIds = useMemo(
    () =>
      eventos.flatMap((ev) =>
        ev.setores.flatMap((s) => s.ingressos.map((i) => i.ingressoId))
      ),
    [eventos]
  )

  const setMany = (ids: number[], on: boolean) => {
    setDraft((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)))
      return next
    })
  }

  const allState = stateOf(allIds, draft)
  const toggleAll = () => setMany(allIds, allState !== 'on')

  return (
    <FilterOverlay>
      <FilterHeader>
        <h3>Ingressos a validar</h3>
        <button className="x" aria-label="Fechar" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </FilterHeader>

      <FilterBody>
        <FilterRow $master onClick={toggleAll}>
          <CheckBox $state={allState}>
            <CheckIcon state={allState} />
          </CheckBox>
          <span className="label">Todos os Ingressos</span>
          <span className="count">{allIds.length}</span>
        </FilterRow>

        {eventos.map((ev) => {
          const evIds = ev.setores.flatMap((s) =>
            s.ingressos.map((i) => i.ingressoId)
          )
          const evState = stateOf(evIds, draft)
          return (
            <div key={ev.eventoId}>
              <FilterRow
                $level={0}
                onClick={() => setMany(evIds, evState !== 'on')}
              >
                <CheckBox $state={evState}>
                  <CheckIcon state={evState} />
                </CheckBox>
                <span className="label">{ev.evento}</span>
                <span className="count">{evIds.length}</span>
              </FilterRow>

              {ev.setores.map((s) => {
                const sIds = s.ingressos.map((i) => i.ingressoId)
                const sState = stateOf(sIds, draft)
                return (
                  <div key={s.setorId}>
                    <FilterRow
                      $level={1}
                      onClick={() => setMany(sIds, sState !== 'on')}
                    >
                      <CheckBox $state={sState}>
                        <CheckIcon state={sState} />
                      </CheckBox>
                      <span className="label">{s.setor}</span>
                      <span className="count">{s.ingressos.length}</span>
                    </FilterRow>

                    {s.ingressos.map((i) => {
                      const on = draft.has(i.ingressoId)
                      return (
                        <FilterRow
                          key={i.ingressoId}
                          $level={2}
                          onClick={() => setMany([i.ingressoId], !on)}
                        >
                          <CheckBox $state={on ? 'on' : 'off'}>
                            <CheckIcon state={on ? 'on' : 'off'} />
                          </CheckBox>
                          <span className="label">{i.ingresso}</span>
                        </FilterRow>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )
        })}
      </FilterBody>

      <FilterFooter>
        <Btn variant="primary" onClick={() => onApply(Array.from(draft))}>
          Aplicar
        </Btn>
      </FilterFooter>
    </FilterOverlay>
  )
}
